<div class="page-head">
  <div>
    <h1 class="title">Settings</h1>
    <p class="desc">Your agency’s profile and branding.</p>
  </div>
</div>

<nav class="tabs mb-4" aria-label="Settings sections" role="tablist">
  @for (tab of tabs; track tab.key) {
    <button
      type="button"
      role="tab"
      class="tab"
      [class.on]="activeTab === tab.key"
      [attr.aria-selected]="activeTab === tab.key"
      (click)="setTab(tab.key)"
    >
      {{ tab.label }}
    </button>
  }
</nav>

@if (!loaded) {
  <div class="card flex flex-col gap-4 p-6">
    <div class="skeleton h-5 w-1/4"></div>
    <div class="skeleton h-9 w-full"></div>
    <div class="skeleton h-9 w-full"></div>
    <div class="skeleton h-9 w-2/3"></div>
  </div>
} @else {
  @switch (activeTab) {
    <!-- ================= Agency information ================= -->
    @case ('agency-info') {
      <form [formGroup]="accountForm" (ngSubmit)="saveChanges()">
        <div class="two-one">
          <div class="card">
            <div class="card-h block">
              <h2 class="card-t">Agency information</h2>
              <p class="card-d">Shown to airlines and on documents issued for your agency.</p>
            </div>
            <div class="card-c">
              <div class="form-grid">
                <div class="field">
                  <label class="label" for="company_name">
                    Company name
                    <span class="req">*</span>
                  </label>
                  <input
                    id="company_name"
                    class="input"
                    formControlName="company_name"
                    placeholder="e.g. AirGateway GmbH"
                    error
                  />
                </div>
                <div class="field">
                  <label class="label" for="phone_contact">
                    Phone number
                    <span class="req">*</span>
                  </label>
                  <input
                    id="phone_contact"
                    class="input"
                    type="tel"
                    formControlName="phone_contact"
                    placeholder="e.g. +491570000000"
                    error
                  />
                </div>
                <div class="field">
                  <label class="label" for="email_contact">
                    Contact email
                    <span class="req">*</span>
                  </label>
                  <input
                    id="email_contact"
                    class="input"
                    type="email"
                    formControlName="email_contact"
                    placeholder="e.g. no-reply@airgateway.com"
                    error
                  />
                </div>
                <div class="field">
                  <label class="label" for="operational_email">
                    Operational email
                    <span class="req">*</span>
                  </label>
                  <input
                    id="operational_email"
                    class="input"
                    type="email"
                    formControlName="operational_email"
                    placeholder="e.g. no-reply@airgateway.com"
                    error
                  />
                  <p class="hint">
                    This email address is used for alerts sent to the agency by the airline.
                  </p>
                </div>
                <div class="field span-all">
                  <label class="label" for="street">
                    Address
                    <span class="req">*</span>
                  </label>
                  <input
                    id="street"
                    class="input"
                    formControlName="street"
                    placeholder="e.g. Gutzkowstr. 8"
                    error
                  />
                </div>
                <div class="field">
                  <label class="label" for="postal_code">
                    Zip code
                    <span class="req">*</span>
                  </label>
                  <input
                    id="postal_code"
                    class="input"
                    formControlName="postal_code"
                    placeholder="e.g. 10827"
                    error
                  />
                </div>
                <div class="field">
                  <label class="label" for="city_name">City</label>
                  <input
                    id="city_name"
                    class="input"
                    formControlName="city_name"
                    placeholder="e.g. Berlin"
                    error
                  />
                </div>
                <div class="field">
                  <label class="label" for="state_prov">State / province</label>
                  <input
                    id="state_prov"
                    class="input"
                    formControlName="state_prov"
                    placeholder="e.g. Berlin"
                    error
                  />
                </div>
              </div>
            </div>
          </div>

          <div class="card self-start">
            <div class="card-h"><h2 class="card-t">Identifiers</h2></div>
            <div class="card-c">
              <dl class="dl">
                <dt>IATA / TIDS</dt>
                <dd>{{ consumerData?.iata_number || '—' }}</dd>
                <dt>City code</dt>
                <dd>{{ consumerData?.city_code || '—' }}</dd>
                <dt>Currency</dt>
                <dd>{{ consumerData?.currency_code || '—' }}</dd>
                <dt>Status</dt>
                <dd>
                  <span class="status" [class.text-muted-foreground]="!agencyActive">
                    <span class="dot" [class.dot-on]="agencyActive"></span>
                    {{ agencyActive ? 'Active' : 'Inactive' }}
                  </span>
                </dd>
              </dl>
              <p class="hint mt-4">Read-only. Contact AirGateway support to change them.</p>
            </div>
          </div>
        </div>

        <div class="savebar">
          @if (accountForm.dirty) {
            <span class="mr-auto text-sm text-muted-foreground">You have unsaved changes</span>
          }
          <button
            type="button"
            class="btn btn-ghost"
            [disabled]="!accountForm.dirty"
            (click)="discardChanges()"
          >
            Discard
          </button>
          <button type="submit" class="btn btn-primary" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </form>
    }

    <!-- ================= Branding ================= -->
    @case ('branding') {
      <form [formGroup]="accountForm" (ngSubmit)="saveChanges()">
        <div class="two-one">
          <div class="flex flex-col gap-6">
            <div class="card">
              <div class="card-h block">
                <h2 class="card-t">Agency logo</h2>
                <p class="card-d">Used on BookingPad and on documents sent to travellers.</p>
              </div>
              <div class="card-c flex flex-wrap items-center gap-6">
                <div
                  class="flex h-32 w-32 items-center justify-center overflow-hidden rounded-xl border border-dashed border-border bg-muted"
                >
                  @if (imageURL) {
                    <img [src]="imageURL" alt="Agency logo" class="h-full w-full object-contain" />
                  } @else {
                    <div class="logo h-[72px] w-[72px] text-2xl">{{ agencyInitials || '—' }}</div>
                  }
                </div>
                <div class="flex flex-col gap-3">
                  <div class="flex flex-wrap gap-2">
                    <button type="button" class="btn btn-outline" (click)="logoInput.click()">
                      <app-icon name="upload" />
                      Upload new logo
                    </button>
                    @if (imageURL) {
                      <button
                        type="button"
                        class="btn btn-ghost text-destructive"
                        (click)="removeLogo()"
                      >
                        Remove
                      </button>
                    }
                  </div>
                  <input
                    #logoInput
                    type="file"
                    accept="image/*"
                    class="hidden"
                    (change)="onLogoSelected($event)"
                  />
                  <p class="hint">PNG, JPG or JPEG · up to 200 × 200 px</p>
                  @if (imageError) {
                    <p class="error">{{ imageError }}</p>
                  }
                </div>
              </div>
            </div>

            <div class="card">
              <div class="card-h block">
                <h2 class="card-t">Brand colour</h2>
                <p class="card-d">
                  Primary colour applied to buttons and headers in your agents’ BookingPad.
                </p>
              </div>
              <div class="card-c flex flex-wrap items-end gap-4">
                <div class="field">
                  <label class="label" for="brand_color_hex">Primary colour</label>
                  <div class="flex items-center gap-2">
                    <input
                      type="color"
                      class="h-9 w-9 cursor-pointer rounded-md border border-input bg-background p-0.5"
                      aria-label="Pick colour"
                      formControlName="brand_color"
                    />
                    <input
                      id="brand_color_hex"
                      class="input w-36 font-mono"
                      formControlName="brand_color"
                    />
                  </div>
                </div>
                <div class="flex items-center gap-1.5 pb-1.5">
                  <span class="text-xs text-muted-foreground">Presets</span>
                  @for (color of brandPresets; track color) {
                    <button
                      type="button"
                      class="h-[22px] w-[22px] rounded-full"
                      [style.background]="color"
                      [style.border]="
                        brandColor.toLowerCase() === color.toLowerCase()
                          ? '2px solid var(--foreground)'
                          : '1px solid var(--border)'
                      "
                      [attr.aria-label]="'Use ' + color"
                      (click)="setBrandColor(color)"
                    ></button>
                  }
                </div>
              </div>
            </div>
          </div>

          <div class="card self-start">
            <div class="card-h">
              <h2 class="card-t">Preview</h2>
              <span class="badge badge-outline">BookingPad</span>
            </div>
            <div class="card-c">
              <div class="overflow-hidden rounded-lg border border-border" aria-hidden="true">
                <div
                  class="flex items-center gap-2.5 px-3 py-2.5 text-white"
                  [style.background]="brandColor"
                >
                  <span class="logo h-6 w-6 bg-white text-[10px]" [style.color]="brandColor">
                    @if (imageURL) {
                      <img [src]="imageURL" alt="" />
                    } @else {
                      {{ agencyInitials }}
                    }
                  </span>
                  <span class="text-sm font-medium">
                    {{ accountForm.get('company_name')?.value || 'Your agency' }}
                  </span>
                </div>
                <div class="flex flex-col gap-2.5 p-3.5">
                  <div class="h-2.5 w-3/5 rounded bg-muted"></div>
                  <div class="h-2.5 w-[85%] rounded bg-muted"></div>
                  <div class="h-2.5 w-2/5 rounded bg-muted"></div>
                  <span
                    class="btn btn-sm mt-1.5 self-start text-white"
                    [style.background]="brandColor"
                  >
                    Search flights
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="savebar">
          @if (accountForm.dirty) {
            <span class="mr-auto text-sm text-muted-foreground">You have unsaved changes</span>
          }
          <button
            type="button"
            class="btn btn-ghost"
            [disabled]="!accountForm.dirty"
            (click)="discardChanges()"
          >
            Discard
          </button>
          <button type="submit" class="btn btn-primary" [disabled]="saving">
            {{ saving ? 'Saving…' : 'Save changes' }}
          </button>
        </div>
      </form>
    }
  }
}
